import React, { useCallback, useMemo, useRef } from "react";
import { cn } from "~/lib/cn";
import { type VariantProps, cva } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import { composeRelAttribute } from "~/lib/shared/link-rel";

import type {
  TrackingCampaign,
  TrackingMedium,
} from "~/lib/monitoring/tracking-parameters";
import { TrackingAction } from "~/lib/monitoring/tracking-parameters";

export const buttonVariants = cva(
  "inline-flex cursor-pointer items-center [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 justify-center rounded-full text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 hover:scale-101 active:scale-99",
  {
    variants: {
      variant: {
        default:
          "bg-gradient-to-b from-purple-600 to-purple-700 border border-purple-800 hover:border-purple-700 text-primary-foreground hover:from-purple-500 hover:to-purple-600 active:from-purple-700 active:to-purple-800 hover:shadow",
        fancy:
          "dcm-shiny bg-purple-700 hover:bg-purple-600 active:bg-purple-800 duration-300 text-primary-foreground duration-800 ease-out-expo rotate-dynamic hover:shadow-md border border-purple-800 hover:border-purple-700",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-purple-800 bg-transparent text-purple-800 hover:bg-white hover:text-purple-700",
        secondary: "bg-brand-50 text-brand-700 hover:bg-brand-50/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        "destructive-ghost": "text-red-600 hover:bg-red-50",
        link: "text-text-primary underline-offset-4 hover:underline",
        orange: "text-white bg-orange-500 hover:bg-orange-300",
        gradient: "bg-purple-800 bg-diamond-gradient text-white",
        zoom: "bg-[#2D8CFF] hover:bg-[#1E7CE8] text-white border border-[#2D8CFF] hover:border-[#1E7CE8]",
        flat: "bg-purple-800 hover:bg-purple-700 text-white border border-purple-800 hover:border-purple-700",
        "flat-animated":
          "bg-purple-800 hover:bg-purple-700 text-white border border-purple-800 hover:border-purple-700 animate-glimmer",
      },
      size: {
        default: "px-4 py-2 h-10",
        sm: "h-9 px-3",
        lg: "px-8 py-4 text-lg border-2",
        "md:lg": "h-9 md:h-auto px-3 md:px-8 md:py-4 md:text-lg md:border-2",
        xl: "px-10 py-4 border-2 text-2xl",
        icon: "h-10 w-10 text-lg",
        "icon-sm": "h-8 w-8 text-sm",
        "icon-nav": "h-12 w-12 text-lg",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    loading?: boolean;
    track?: {
      campaign: TrackingCampaign;
      medium: TrackingMedium;
    };
  };

export const Button = React.memo<ButtonProps>(
  ({
    size,
    variant: look,
    onClick,
    track,
    loading,
    children,
    className,
    ...props
  }) => {
    const buttonRef = useRef<HTMLButtonElement>(null);

    const trackClick = useCallback(
      (_e: React.MouseEvent<HTMLButtonElement>) => {
        if (window && track && buttonRef.current) {
          window.dataLayer?.push({
            event: "event",
            eventCampaign: track.campaign,
            eventAction: TrackingAction.Click,
            eventMedium: track.medium,
            eventLabel: buttonRef.current.innerText,
          });
        }
      },
      [track],
    );

    const handleMouseMove = useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        if ("fancy" !== look) {
          return;
        }
        const rotateAmount = 60;

        const button = buttonRef.current;
        if (!button) {
          return;
        }

        const rect = button.getBoundingClientRect();

        // Calculate relative mouse position within the button
        const xRatio = (e.clientX - rect.left) / rect.width;
        const yRatio = (e.clientY - rect.top) / rect.height;

        // Apply the shimmer effect
        button.style.setProperty("--e1x", `${30 + xRatio * 60}%`);
        button.style.setProperty("--e1y", `${5 + yRatio * 40}%`);
        button.style.setProperty("--e2x", `${35 + xRatio * 20}%`);
        button.style.setProperty("--e2y", `${35 + yRatio * 20}%`);

        // Calculate the button's center point
        const rotateX = -(e.clientY - rect.y - rect.height / 2) / rotateAmount;
        const rotateY = (e.clientX - rect.x - rect.width / 2) / rotateAmount;

        // Apply 3D rotation variables
        button.style.setProperty("--rotateX", `${rotateX}deg`);
        button.style.setProperty("--rotateY", `${rotateY}deg`);
      },
      [look],
    );

    const handleClick = useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        if (track) {
          trackClick(e);
        }
        if (onClick) {
          onClick(e);
        }
      },
      [track, trackClick, onClick],
    );

    const buttonClassName = useMemo(
      () => cn(buttonVariants({ size, variant: look }), className),
      [size, look, className],
    );

    return (
      <button
        ref={buttonRef}
        onMouseMove={handleMouseMove}
        {...props}
        onClick={handleClick}
        className={buttonClassName}
        disabled={loading || props.disabled}
      >
        {loading && <Loader2 className="mr-1 size-4 animate-spin" />}
        {children}
      </button>
    );
  },
);

Button.displayName = "Button";

export type ButtonLinkProps = React.AnchorHTMLAttributes<HTMLAnchorElement> &
  VariantProps<typeof buttonVariants> & {
    track?: {
      campaign: TrackingCampaign;
      medium: TrackingMedium;
    };
    rel?: string;
    target?: React.HTMLAttributeAnchorTarget;
    href?: string;
  };

export const ButtonLink = ({
  size,
  variant: look,
  track,
  className,
  children,
  rel,
  target,
  href,
  onClick,
  ...props
}: ButtonLinkProps) => {
  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (typeof window !== "undefined" && track) {
      const anchor = event.currentTarget;
      window.dataLayer?.push({
        event: "event",
        eventCampaign: track.campaign,
        eventAction: TrackingAction.Click,
        eventMedium: track.medium,
        eventLabel: anchor.innerText,
      });
    }

    onClick?.(event);
  };

  const safeRel = composeRelAttribute({
    rel,
    target,
    href,
    includeNofollowForExternal: true,
  });

  return (
    <a
      {...props}
      href={href}
      target={target}
      rel={safeRel}
      onClick={track || onClick ? handleClick : undefined}
      className={cn(buttonVariants({ size, variant: look }), className)}
    >
      {children}
    </a>
  );
};
